import { NavLink, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectAllowedMenus } from "../store/selectors";
import { selectUser } from "../store/authSlice";
import logo from "../logo_nav.svg";

// Menus hidden from the sidebar (routes stay reachable by URL)
const HIDDEN_MENU_PATHS = ["/admin/cms-pages"];

const linkClass =({ isActive }) =>
  `block rounded-lg px-3 py-2 text-sm transition ${
    isActive ? "bg-gray-100 font-semibold" : "hover:bg-gray-100"
  }`;

function MenuNode({ item, openGroups, toggleGroup, pathToOpenKey, onNavigate }) {
  const hasChildren = item.children && item.children.length > 0;

  if (hasChildren) {
    const isOpen = openGroups[item.slug];
    const isActive = pathToOpenKey[item.slug];
    return (
      <div>
        <button
          type="button"
          onClick={() => toggleGroup(item.slug)}
          className={`w-full text-left rounded-lg px-3 py-2 text-sm transition flex items-center justify-between ${
            isActive ? "bg-gray-100 font-semibold" : "hover:bg-gray-100"
          }`}
        >
          <span>{item.name}</span>
          <span className="text-xs">{isOpen ? "▲" : "▼"}</span>
        </button>
        {isOpen && (
          <ul className="pl-4 space-y-1 list-none">
            {item.children.map((child) => (
              <li key={child.menu_id || child.slug}>
                <NavLink to={child.path} className={linkClass} onClick={onNavigate}>
                  {child.name}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <NavLink
      key={item.slug}
      to={item.path}
      end={item.path === "/"}
      className={linkClass}
      onClick={onNavigate}
    >
      {item.name}
    </NavLink>
  );
}

export default function Sidebar({ isOpen, onClose }) {
  const loc = useLocation();
  const rawMenus = useSelector(selectAllowedMenus);
  const allowedMenus = useMemo(
    () =>
      rawMenus.filter(
        (m) => !HIDDEN_MENU_PATHS.includes(m.path) && m.name !== "CMS Pages"
      ),
    [rawMenus]
  );
  const user = useSelector(selectUser);
  const isSuperadmin = user && Number(user.role_id) === 1;

  const pathToOpenKey = useMemo(() => {
    const map = {};
    allowedMenus.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const isOpen = item.children.some(
          (c) => loc.pathname === c.path || (c.path && loc.pathname.startsWith(c.path))
        );
        map[item.slug] = isOpen;
      }
    });
    return map;
  }, [allowedMenus, loc.pathname]);

  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      Object.keys(pathToOpenKey).forEach((slug) => {
        if (pathToOpenKey[slug]) next[slug] = true;
      });
      return next;
    });
  }, [pathToOpenKey]);

  const toggleGroup = (slug) => {
    setOpenGroups((p) => ({ ...p, [slug]: !p[slug] }));
  };

  // Find the index of the "users" menu to inject Media Manager right after it
  const usersIndex = allowedMenus.findIndex(
    (m) => m.slug === "users" || m.path === "/users"
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 border-r bg-white p-4 overflow-y-auto transform transition-transform duration-200 ease-out
        lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:z-auto
        ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="mb-6 flex items-center justify-between gap-2">
          <img src={logo} alt="Navigatio" className="h-8 w-auto" />
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden rounded-lg p-2 hover:bg-gray-100"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
        <nav className="space-y-1">
          {allowedMenus.map((item, idx) => (
            <div key={item.slug}>
              <MenuNode
                item={item}
                openGroups={openGroups}
                toggleGroup={toggleGroup}
                pathToOpenKey={pathToOpenKey}
                onNavigate={onClose}
              />
              {/* {isSuperadmin && idx === usersIndex && (
                <div className="mt-1 space-y-1">
                  <NavLink to="/admin/media-manager" className={linkClass} onClick={onClose}>
                    Media Manager
                  </NavLink>
                  <NavLink to="/admin/png-to-gif" className={linkClass} onClick={onClose}>
                    PNG to GIF Converter
                  </NavLink>
                </div>
              )} */}
            </div>
          ))}

          {/* Fallback: if "users" menu not found, append at end */}
          {isSuperadmin && usersIndex === -1 && (
            <div className="space-y-1">
              <NavLink to="/admin/media-manager" className={linkClass} onClick={onClose}>
                Media Manager
              </NavLink>
              <NavLink to="/admin/png-to-gif" className={linkClass} onClick={onClose}>
                PNG to GIF Converter
              </NavLink>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
}
